import type { SRT_RowData, SRT_TableInstance } from '../../master-table'

import { RotateCcwIcon, SettingsIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useBackupPreset } from '@/shared/lib/datasheet/lib/useBackupPreset'
import { useColumnsPreset } from '@/shared/lib/datasheet/lib/useColumnsPreset'

import {
	SRT_TableColumnsConfig as ColumnsConfig,
	SRT_TableColumnsConfigReorderingListWithSearch as ColumnsConfigList,
	SRT_TableColumnsConfigSearch as ColumnsConfigSearch,
} from '../../master-table'
import { Alert, AlertDescription } from '../../ui/alert'
import { Button } from '../../ui/button'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '../../ui/sheet'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip'

export function ColumnsDrawer<T extends SRT_RowData>({
	table,
}: {
	table: SRT_TableInstance<T>
}) {
	const { t } = useTranslation()
	const { activePreset } = useColumnsPreset()
	const { handleLoadDefaultPreset } = useBackupPreset()
	const { localization } = table.options

	return (
		<Sheet modal={false}>
			<div className='relative'>
				<SheetTrigger asChild>
					<Button size='icon'>
						<SettingsIcon />
					</Button>
				</SheetTrigger>
				{activePreset !== '__my__' && (
					<span className='absolute -top-0.5 -right-0.5 z-10 size-2 rounded-full bg-warning ring-[3px] ring-warning/30' />
				)}
			</div>
			<SheetContent disbleOverlay className='gap-0'>
				<SheetHeader className='pb-2'>
					<SheetTitle>{localization.showHideColumns}</SheetTitle>
				</SheetHeader>
				<ColumnsConfig table={table}>
					<div className='flex flex-col overflow-hidden pt-1'>
						{activePreset !== '__my__' && (
							<div className='px-4 pb-2'>
								<Alert variant='warning' className='p-2'>
									<AlertDescription className='text-xs'>
										{t('reports.preset.alert_description', {
											defaultValue:
												'You are using a columns preset from the report . Your changes will not be saved. If you want to save your changes, you can create a new report or back to the free view.',
										})}
									</AlertDescription>
								</Alert>
							</div>
						)}
						<div className='flex flex-col px-4 pb-2'>
							<div className='flex items-center justify-between gap-1'>
								<ColumnsConfigSearch table={table} />
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant='secondary'
											size='icon'
											onClick={() => handleLoadDefaultPreset()}
										>
											<RotateCcwIcon />
										</Button>
									</TooltipTrigger>
									<TooltipContent>{localization.resetColumns}</TooltipContent>
								</Tooltip>
							</div>
						</div>

						<ColumnsConfigList table={table} />
					</div>
				</ColumnsConfig>
			</SheetContent>
		</Sheet>
	)
}
